import { Timecard } from '../types/Timecard';
import { ForemanAreaApprovalData } from '../types/ApprovalData';

/**
 * Transforms an array of timecards into approval data grouped by foreman area.
 * Groups timecards by homeForemanAreaId and counts pending_approval and revisions_requested statuses.
 * 
 * @param timecards - Array of timecard objects
 * @returns Array of ForemanAreaApprovalData sorted by total count (descending)
 */
export const transformTimecardsToApprovalData = (
  timecards: Timecard[]
): ForemanAreaApprovalData[] => {
  if (!timecards || timecards.length === 0) {
    return [];
  }

  // Helper function to check if status matches (case-insensitive)
  const matchesStatus = (status: string, target: string): boolean => {
    if (!status) return false;
    const statusLower = status.toLowerCase();
    const targetLower = target.toLowerCase();
    return statusLower === targetLower ||
           statusLower.includes(targetLower) ||
           (statusLower.includes('pending') && statusLower.includes('approval')) ||
           (statusLower.includes('revision'));
  };

  // Group timecards by foreman area ID
  const grouped = timecards.reduce((acc, timecard) => {
    const areaId = timecard.homeForemanAreaId;

    // Skip if foreman area data is missing
    if (!timecard.homeForemanArea) {
      return acc;
    }

    // Initialize entry for this foreman area if it doesn't exist
    if (!acc[areaId]) {
      acc[areaId] = {
        foremanArea: timecard.homeForemanArea,
        pendingApprovals: 0,
        revisionsRequested: 0,
      };
    }

    // Count by status (case-insensitive matching)
    const statusLower = timecard.status?.toLowerCase() || '';
    if (matchesStatus(timecard.status, 'pending_approval') ||
        matchesStatus(timecard.status, 'pending approval') ||
        statusLower === 'pending' ||
        (statusLower.includes('pending') && statusLower.includes('approval'))) {
      acc[areaId].pendingApprovals++;
    } else if (matchesStatus(timecard.status, 'revisions_requested') ||
               matchesStatus(timecard.status, 'revisions requested') ||
               statusLower === 'revisions completed' ||
               statusLower.includes('revision')) {
      acc[areaId].revisionsRequested++;
    }

    return acc;
  }, {} as Record<number, ForemanAreaApprovalData>);

  // Convert to array and sort by total count (descending)
  const result = Object.values(grouped);
  result.sort((a, b) => {
    const totalA = a.pendingApprovals + a.revisionsRequested;
    const totalB = b.pendingApprovals + b.revisionsRequested;
    return totalB - totalA;
  });

  return result;
};
